import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LESSON_DEFAULTS = {
  BLUEPRINTS: {
    opening_p1: 'Before Jesus walked the earth, God gave His people seven holy days — Mo\'edim, divine appointments — and commanded them to rehearse those days for centuries. Each one was a blueprint. When Jesus came, He fulfilled the first four of those feasts on the exact day they had been rehearsed for centuries. Not approximately. Exact.',
    opening_p2: 'And the last three are still on the calendar.',
    spring_title: 'THE SPRING FEASTS — FULFILLED',
    spring_subtitle: 'Four rehearsals. Four fulfillments. On the exact day each was appointed.',
    fall_title: 'THE FALL FEASTS — STILL ON THE CALENDAR',
    fall_subtitle: 'Three appointments remain. The blueprint is still running.',
    pattern_text: 'If God fulfilled the first four feasts on the exact appointed day — to the letter, to the hour — then the remaining three feasts are not metaphors. They are not suggestions. They are appointments. And they are still on the calendar. This is the box top of the puzzle. This is what the completed picture looks like.',
    git_who: 'God, commanding His people through Moses.',
    git_what: 'Seven appointed times to be rehearsed "forever" — fulfilled by Messiah on the exact day.',
    git_when: 'Four complete. Three remaining. The Feast of Trumpets — the "last trump" — is next on the calendar.',
    bridge_text: 'You have the blueprint. You know what God has already done and what remains. Now there\'s one more key before we open the corners.',
    bridge_question: 'How is the book of Revelation actually written?',
  },
  GIT_101: {
    opening_verse: '"These were more noble than those in Thessalonica, in that they received the word with all readiness of mind, and searched the scriptures daily, whether those things were so."',
    opening_verse_ref: 'Acts 17:11',
    secondary_verse: '"For precept must be upon precept, precept upon precept, line upon line, line upon line: here a little, and there a little."',
    secondary_verse_ref: 'Isaiah 28:10',
    opening_p1: 'Those people were called noble — not because they were smarter or more educated, but because they were willing to check.',
    opening_p2: "Solomon explained the importance of using wisdom and knowledge of the basic principle of grammar to understand the word of God.",
    what_is_git_p1: 'The Grammar Integrity Test is a three-question tool you can apply to any verse in your Bible.',
    what_is_git_p2: "Three questions. That's it. The same three questions every time.",
    illustration_intro: "Before we test this on a Bible verse, let's make sure the tool is clear. Read this sentence:",
    illustration_sentence: '"The dog cannot go outside until it stops raining."',
    illustration_conclusion: 'That is the whole test. Three questions.',
    encouragement_text: 'You have been doing this your whole life.',
    berean_text: 'The Bereans in Acts 17 were called noble for one reason: they were willing to check.',
    bridge_text: "You have the tool. Now let's pick up the second key — the Blueprints.",
    bridge_next: 'Next: Legend Key 2 — God\'s Divine Rehearsals',
  },
  RECAPITULATION: {
    opening_p1: 'There is an argument for a pre-tribulation rapture that does not come from a single verse — it comes from the structure of Revelation itself.',
    opening_p2: 'John wrote it the way Hebrew authors have always written — with recapitulation.',
    what_is_recap_text: "Recapitulation means the author retells the same period from a different angle.",
    diagram_note: 'All three series terminate at the same event. Not three different endpoints — one.',
    rules_out_text: "The pretrib structural argument requires strict linear sequence.",
    git_who: "John, writing what he was shown.",
    git_what: "Seals, Trumpets, and Bowls that converge at the same terminus.",
    git_when: "All three seventh positions arrive at the same moment.",
    appendix_code: 'APP-BM', appendix_title: 'Appendix BM: The Architecture of Revelation',
    appendix_desc: 'A complete structural layout of the book of Revelation.',
    bridge_summary_git: 'The GIT — your reading tool.',
    bridge_summary_bp: "The Blueprints — God's appointed calendar.",
    bridge_summary_recap: 'Recapitulation — the structure of Revelation.',
    bridge_next: 'Open 2 Thessalonians 2. The first corner piece.',
  },
  CORNER_1: {
    verse_ref_label: '2 THESSALONIANS 2:1–4',
    opening_p1: 'This is the first corner piece because it contains the clearest grammatical lock in the entire New Testament.',
    opening_p2: 'If you have been taught that the church will be gathered before the tribulation begins, this passage alone creates an irreconcilable problem.',
    verse_text: 'Now we beseech you, brethren, by the coming of our Lord Jesus Christ...',
    anchor_phrase: 'shall not come, except',
    teacher_voice: 'Paul was worried...', professor_voice: 'The Greek construction here is emphatic...',
    pastoral_voice: 'Paul loved these people...', rules_out: 'Any gathering of the church before the Antichrist is revealed.',
    reading_hook_preview: 'Paul did not leave the timing of the gathering to guesswork.',
  },
  CORNER_2: {
    verse_ref_label: 'MATTHEW 24:29–31',
    opening_p1: "The first corner piece showed us Paul's grammatical lock.",
    opening_p2: 'In the Olivet Discourse, Jesus was asked directly: when will these things happen?',
    verse_text: 'Immediately after the tribulation of those days...',
    anchor_phrase: 'Immediately after the tribulation',
    teacher_voice: 'This is Jesus talking...', professor_voice: 'The Greek phrase is "eutheos meta"...',
    pastoral_voice: 'Imagine being one of those disciples...', rules_out: 'Any gathering of the elect before or during the tribulation.',
    reading_hook_preview: 'Jesus described one arrival — after the tribulation.',
  },
  CORNER_3: {
    verse_ref_label: '1 CORINTHIANS 15:51–52',
    opening_p1: "The first corner gave us Paul's absolute condition.",
    opening_p2: 'The word is "last." And it changes everything.',
    verse_text: 'Behold, I shew you a mystery...',
    anchor_phrase: 'at the last trump',
    teacher_voice: 'Paul calls it a mystery...', professor_voice: 'The word Paul uses is "eschate"...',
    pastoral_voice: '"In a moment, in the twinkling of an eye."',
    rules_out: "Any gathering at a trumpet that is not the final one.",
    reading_hook_preview: 'Paul calls it a mystery — and the mystery is the timing.',
  },
  CORNER_4: {
    verse_ref_label: 'REVELATION 20:4–6',
    opening_p1: 'Three corners are now in place.',
    opening_p2: 'The question this corner answers: if the church is resurrected before the tribulation, what do you do with a verse that calls the post-tribulation resurrection "the first"?',
    verse_text: 'And I saw thrones, and they sat upon them...',
    anchor_phrase: 'This is the first resurrection',
    teacher_voice: 'This is the corner piece that locks the entire frame.',
    professor_voice: 'The phrase "haute he anastasis he prote"...',
    pastoral_voice: 'There is something deeply moving about this passage.',
    rules_out: "Any prior resurrection of the righteous.",
    reading_hook_preview: 'John describes people who endured the tribulation.',
  },
};

const FEAST_DEFAULTS = {
  BLUEPRINTS: {
    spring_feasts: [
      { name: 'Passover', hebrew: 'Pesach', ref: 'Exodus 12', fulfilled: 'Jesus crucified on the exact day of Passover — the Lamb of God slain.' },
      { name: 'Unleavened Bread', hebrew: 'Chag HaMatzot', ref: 'Leviticus 23:6–14', fulfilled: 'Jesus buried, His body did not see corruption — the sinless bread.' },
      { name: 'Firstfruits', hebrew: 'Bikkurim', ref: 'Leviticus 23:9–14', fulfilled: 'Jesus rose on the exact day of Firstfruits — the first of those who sleep.' },
      { name: 'Pentecost', hebrew: 'Shavuot', ref: 'Leviticus 23:15–22', fulfilled: 'The Holy Spirit fell on the exact day of Pentecost — 50 days after Firstfruits.' },
    ],
    fall_feasts: [
      { name: 'Trumpets', hebrew: 'Yom Teruah', ref: 'Leviticus 23:23–25', description: 'A day of shouting and trumpet blasts. The last trump. The gathering.' },
      { name: 'Atonement', hebrew: 'Yom Kippur', ref: 'Leviticus 23:26–32', description: 'The day of covering. National repentance. Israel looks upon the one they pierced.' },
      { name: 'Tabernacles', hebrew: 'Sukkot', ref: 'Leviticus 23:33–44', description: 'God dwells with His people. The Millennial Kingdom. Emmanuel.' },
    ],
  },
  GIT_101: {
    git_questions: [
      { num: '1', question: 'WHO?', label: 'The Subject', desc: 'Who is being talked about?' },
      { num: '2', question: 'WHAT?', label: 'The Verb / Action', desc: 'What is happening?' },
      { num: '3', question: 'WHEN?', label: 'The Timeline / Condition', desc: 'When does this happen?' },
    ],
    dog_answers: [
      { q: 'WHO?', a: "The dog. That's the subject." },
      { q: 'WHAT?', a: "Cannot go outside. That's the verb." },
      { q: 'WHEN?', a: 'Until it stops raining.' },
    ],
    git_table: [
      { is: 'A method for reading what the text says.', isnt: 'A new theological system.' },
      { is: 'A reading tool.', isnt: 'A replacement for study.' },
      { is: '', isnt: 'An argument.' },
    ],
  },
  RECAPITULATION: {
    convergence_points: [
      { title: 'The Seventh Position', desc: 'The 7th Seal, Trumpet, and Bowl all terminate at the same event.', refs: 'Revelation 8:1, Revelation 11:15–18, Revelation 16:17–21' },
      { title: 'The Sixth-Position Euphrates', desc: 'Same river. Same armies. Two descriptions.', refs: 'Revelation 9:13–16, Revelation 16:12–16' },
      { title: 'Revelation 16:15 — The Intrusion', desc: '"Behold, I come as a thief." Spoken during the bowls.', refs: 'Revelation 16:15, 1 Thessalonians 5:2' },
    ],
  },
  CORNER_1: {
    git_answers: [
      { q: 'WHO?', a: 'Paul is writing to the church at Thessalonica.' },
      { q: 'WHAT?', a: '"That day shall not come, except..."' },
      { q: 'WHEN?', a: 'The timing word is "EXCEPT."' },
    ],
    discussion_questions: [
      { text: 'Paul says "let no man deceive you by any means." What does that tell you?' },
      { text: 'If the gathering cannot come until the man of sin is revealed, what does that mean?' },
      { text: 'Why would Paul comfort a frightened church by telling them to watch for the Antichrist?' },
      { text: 'Paul had already taught them this sequence in person. Why is this doctrine important enough to teach twice?' },
    ],
  },
  CORNER_2: {
    git_answers: [
      { q: 'WHO?', a: 'Jesus. He is the one speaking — directly, to His disciples.' },
      { q: 'WHAT?', a: '"He shall send his angels with a great sound of a trumpet."' },
      { q: 'WHEN?', a: '"IMMEDIATELY AFTER the tribulation of those days."' },
    ],
    discussion_questions: [
      { text: 'Jesus described the gathering with angels, a trumpet, and clouds. If they are different events, why are all the elements identical?' },
      { text: 'Why does Jesus begin the Olivet Discourse with "Take heed that no man deceive you"?' },
      { text: "If the gathering is after the tribulation, what does that mean for the church's role?" },
      { text: 'Jesus says "his elect" — who are the elect?' },
    ],
  },
  CORNER_3: {
    git_answers: [
      { q: 'WHO?', a: 'Paul is writing to the church at Corinth — believers.' },
      { q: 'WHAT?', a: 'Three things happen simultaneously at the last trumpet.' },
      { q: 'WHEN?', a: '"At the LAST trump." The word is eschatos — final.' },
    ],
    discussion_questions: [
      { text: 'Can the rapture trumpet be the "last" trumpet if seven more trumpets follow?' },
      { text: 'Both 1 Cor 15:52 and 1 Thess 4:16 describe the same event. Why separate them?' },
      { text: "Read Revelation 11:15–18. What happens at the seventh trumpet?" },
      { text: 'Should we be watching for escape — or for the trumpet?' },
    ],
  },
  CORNER_4: {
    git_answers: [
      { q: 'WHO?', a: 'Tribulation martyrs who refused the mark of the beast.' },
      { q: 'WHAT?', a: '"This is the first resurrection."' },
      { q: 'WHEN?', a: 'After the tribulation. John calls it THE FIRST.' },
    ],
    discussion_questions: [
      { text: 'If the post-tribulation resurrection is "the first," can there be an earlier one?' },
      { text: 'Does the grammar of Revelation 20:5–6 support a "phased" first resurrection?' },
      { text: 'All four corner pieces describe the same event. How does each passage confirm the others?' },
      { text: 'What does the completed frame tell you about the timing of the gathering?' },
    ],
  },
};

export default function AdminLessonContent() {
  const { moduleKey } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const theme = useTheme();
  const [schema, setSchema] = useState(null);
  const [sections, setSections] = useState({});
  const [feasts, setFeasts] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }, [token]);

  useEffect(() => {
    if (!moduleKey) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/lesson-content/${moduleKey}`, { headers: headers(), credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSchema(data.schema);
          const defaults = LESSON_DEFAULTS[moduleKey] || {};
          const savedSections = data.content || {};
          // Merge: saved content overrides defaults
          const merged = { ...defaults };
          for (const [k, v] of Object.entries(savedSections)) {
            if (v !== undefined && v !== '') merged[k] = v;
          }
          setSections(merged);

          const feastDefaults = FEAST_DEFAULTS[moduleKey] || {};
          const savedFeasts = data.feasts || {};
          const mergedFeasts = {};
          for (const key of Object.keys(feastDefaults)) {
            mergedFeasts[key] = savedFeasts[key]?.length > 0 ? savedFeasts[key] : feastDefaults[key];
          }
          setFeasts(mergedFeasts);
        }
      } catch (e) {
        toast.error('Failed to load lesson content');
      }
      setLoading(false);
    })();
  }, [moduleKey, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/lesson-content/${moduleKey}`, {
        method: 'PUT', headers: headers(), credentials: 'include',
        body: JSON.stringify({ sections, feasts }),
      });
      if (res.ok) {
        toast.success('Lesson content saved!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Save failed');
      }
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const updateFeastItem = (feastKey, index, field, value) => {
    setFeasts(prev => {
      const items = [...(prev[feastKey] || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, [feastKey]: items };
    });
  };

  const addFeastItem = (feastKey) => {
    setFeasts(prev => {
      const items = [...(prev[feastKey] || [])];
      const fields = schema?.feast_sections?.find(f => f.key === feastKey)?.fields || [];
      const newItem = {};
      fields.forEach(f => newItem[f] = '');
      items.push(newItem);
      return { ...prev, [feastKey]: items };
    });
  };

  const removeFeastItem = (feastKey, index) => {
    setFeasts(prev => {
      const items = [...(prev[feastKey] || [])];
      items.splice(index, 1);
      return { ...prev, [feastKey]: items };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bgPrimary }}>
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: theme.bgPrimary, color: theme.textPrimary }}>
      <nav className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: theme.borderPrimary }}>
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm" style={{ color: theme.navText }} data-testid="btn-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-xs tracking-widest uppercase" style={{ color: theme.textMuted }}>Lesson Content Editor</span>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all"
          style={{ background: `${theme.accentPrimary}18`, borderColor: `${theme.accentPrimary}40`, color: theme.accentPrimary }}
          data-testid="btn-save-content">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-8">
        <h1 className="text-2xl font-bold mb-2" style={{ color: theme.accentPrimary }} data-testid="content-editor-title">
          {schema?.title || moduleKey}
        </h1>
        <p className="text-sm mb-8" style={{ color: theme.textMuted }}>
          Edit the content sections below. Changes are saved to the database and override the defaults.
        </p>

        {/* Text Sections */}
        {schema?.sections?.map(field => (
          <div key={field.key} className="mb-6" data-testid={`field-${field.key}`}>
            <label className="block text-xs tracking-wider uppercase mb-2 font-bold" style={{ color: theme.labelColor }}>
              {field.label}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                value={sections[field.key] || ''}
                onChange={(e) => setSections(prev => ({ ...prev, [field.key]: e.target.value }))}
                rows={4}
                className="w-full rounded-xl border px-4 py-3 text-sm leading-relaxed resize-y"
                style={{ background: 'rgba(10,8,4,0.6)', borderColor: theme.borderPrimary, color: theme.textPrimary }}
              />
            ) : (
              <input
                type="text"
                value={sections[field.key] || ''}
                onChange={(e) => setSections(prev => ({ ...prev, [field.key]: e.target.value }))}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ background: 'rgba(10,8,4,0.6)', borderColor: theme.borderPrimary, color: theme.textPrimary }}
              />
            )}
          </div>
        ))}

        {/* Feast Sections */}
        {schema?.feast_sections?.map(feastSchema => (
          <div key={feastSchema.key} className="mb-8" data-testid={`feast-section-${feastSchema.key}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm tracking-wider uppercase font-bold" style={{ color: theme.accentPrimary }}>
                {feastSchema.label}
              </h2>
              <button onClick={() => addFeastItem(feastSchema.key)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border transition-all"
                style={{ borderColor: `${theme.accentPrimary}30`, color: theme.accentPrimary }}>
                <Plus className="w-3 h-3" /> Add Item
              </button>
            </div>
            <div className="space-y-3">
              {(feasts[feastSchema.key] || []).map((item, idx) => (
                <div key={idx} className="rounded-xl border p-4 relative" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <button onClick={() => removeFeastItem(feastSchema.key, idx)}
                    className="absolute top-3 right-3 p-1 rounded hover:opacity-80" style={{ color: '#ef4444' }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <div className="grid grid-cols-2 gap-3 pr-8">
                    {feastSchema.fields.map(f => (
                      <div key={f}>
                        <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: theme.textMuted }}>{f}</label>
                        <input type="text" value={item[f] || ''}
                          onChange={(e) => updateFeastItem(feastSchema.key, idx, f, e.target.value)}
                          className="w-full rounded-lg border px-3 py-2 text-sm"
                          style={{ background: 'rgba(10,8,4,0.8)', borderColor: theme.borderPrimary, color: theme.textPrimary }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Preview link */}
        <div className="mt-8 pt-6 border-t text-center" style={{ borderColor: theme.borderPrimary }}>
          <button onClick={() => navigate(`/lesson/${moduleKey.toLowerCase().replace('_', '-').replace('git_101', 'git-101')}`)}
            className="text-sm underline" style={{ color: theme.accentPrimary }} data-testid="btn-preview-lesson">
            Preview this lesson
          </button>
        </div>
      </div>
    </div>
  );
}
